import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button, Card, Ring, StatusPill, SegmentedControl, OBLIGATION_STATUS, WORK_ORDER_STATUS, CRITICALITY, AVAILABILITY, cn, formatCurrency,
} from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';
import { useOrg } from '../lib/org';

type Asset = {
  id: string; name: string; serial_number: string | null; qr_token: string; agent_type: string | null;
  criticality: string; availability: string; manufacture_date: string | null; commissioned_date: string | null;
  property_id: string; properties: { name: string } | null; asset_categories: { name: string; code: string } | null;
  zones: { name: string } | null;
};
type Obl = { id: string; obligation_type: string; due_date: string | null; status: string; last_completed_at: string | null;
  asset_obligation_rules: { rule_code: string; name_i18n: Record<string, string>; interval_months: number | null; interval_days: number | null; basis: string } | null };
type Audit = { id: number; action: string; changed_at: string; table_name: string };

const AUDIT_TONE: Record<string, string> = {
  insert: 'var(--pv-st-done-fg)', update: 'var(--pv-st-inprogress-fg)',
  soft_delete: 'var(--pv-st-overdue-fg)', delete: 'var(--pv-st-overdue-fg)', restore: 'var(--pv-st-onhold-fg)',
};

export function AssetPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { id: orgId, timezone, currency } = useOrg();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [obl, setObl] = useState<Obl[]>([]);
  const [activity, setActivity] = useState<Audit[]>([]);
  const [tab, setTab] = useState(0);
  const [wos, setWos] = useState<{ id: string; wo_number: string | null; wo_type: string; status: string; created_at: string }[]>([]);
  const [warranties, setWarranties] = useState<{ id: string; guarantor: string | null; starts_on: string | null; ends_on: string | null }[]>([]);
  const [docs, setDocs] = useState<{ id: string; filename: string | null; kind: string; created_at: string }[]>([]);
  const [costTotal, setCostTotal] = useState(0);
  const [uploading, setUploading] = useState(false);

  const loadExtras = async (assetId: string, propertyId: string) => {
    const [w, wa, d] = await Promise.all([
      supabase.from('work_orders').select('id,wo_number,wo_type,status,created_at').eq('asset_id', assetId).is('deleted_at', null).order('created_at', { ascending: false }).limit(50),
      supabase.from('warranties').select('id,guarantor,starts_on,ends_on').eq('asset_id', assetId).is('deleted_at', null),
      supabase.from('asset_documents').select('id,filename,kind,created_at').eq('asset_id', assetId).is('deleted_at', null).order('created_at', { ascending: false }),
    ]);
    const woList = (w.data as any[]) ?? [];
    setWos(woList as any); setWarranties((wa.data as any[]) ?? []); setDocs((d.data as any[]) ?? []);
    if (woList.length) {
      const cl = await supabase.from('work_order_cost_lines').select('line_type,quantity,unit_cost,hourly_rate').in('work_order_id', woList.map((x) => x.id)).is('deleted_at', null);
      const total = ((cl.data as any[]) ?? []).reduce((s, l) => s + (l.line_type === 'labour' ? (l.hourly_rate ?? 0) : (l.unit_cost ?? 0)) * (l.quantity ?? 0), 0);
      setCostTotal(total);
    } else setCostTotal(0);
  };

  const load = async () => {
    if (!id) return;
    const [a, o, au] = await Promise.all([
      supabase.from('assets').select('id,name,serial_number,qr_token,agent_type,criticality,availability,manufacture_date,commissioned_date,property_id,properties(name),asset_categories(name,code),zones(name)').eq('id', id).single(),
      supabase.from('asset_obligations').select('id,obligation_type,due_date,status,last_completed_at,asset_obligation_rules(rule_code,name_i18n,interval_months,interval_days,basis)').eq('asset_id', id).is('deleted_at', null).order('due_date', { nullsFirst: false }),
      supabase.from('audit_log').select('id,action,changed_at,table_name').eq('row_id', id).order('changed_at', { ascending: false }).limit(6),
    ]);
    const as = a.data as unknown as Asset;
    setAsset(as);
    setObl((o.data as unknown as Obl[]) ?? []);
    setActivity((au.data as Audit[]) ?? []);
    if (as) loadExtras(as.id, as.property_id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function uploadDoc(file: File) {
    if (!asset) return;
    setUploading(true);
    const path = `${orgId}/${asset.property_id}/${asset.id}/${Date.now()}-${file.name}`;
    const up = await supabase.storage.from('assets').upload(path, file);
    if (!up.error) {
      await supabase.from('asset_documents').insert({ organization_id: orgId, property_id: asset.property_id, asset_id: asset.id, kind: file.type.startsWith('image/') ? 'photo' : 'document', storage_path: path, filename: file.name, mime_type: file.type } as any);
      loadExtras(asset.id, asset.property_id);
    }
    setUploading(false);
  }

  const lifecycle = useMemo(() => {
    if (!asset?.manufacture_date) return null;
    const start = new Date(asset.manufacture_date).getTime();
    const disposal = obl.find((o) => o.asset_obligation_rules?.rule_code?.startsWith('disposal'));
    const end = disposal?.due_date ? new Date(disposal.due_date).getTime() : start + 12 * 365.25 * 864e5;
    const pct = Math.max(0, Math.min(100, Math.round(((Date.now() - start) / (end - start)) * 100)));
    return { startYear: new Date(start).getFullYear(), endYear: new Date(end).getFullYear(), pct };
  }, [asset, obl]);

  const nextDue = obl.filter((o) => o.due_date).sort((a, b) => (a.due_date! < b.due_date! ? -1 : 1))[0];
  const lastDone = obl.filter((o) => o.last_completed_at).sort((a, b) => (a.last_completed_at! > b.last_completed_at! ? -1 : 1))[0];

  const oblName = (o: Obl) =>
    o.asset_obligation_rules?.name_i18n?.[i18n.language] ??
    o.asset_obligation_rules?.name_i18n?.en ??
    o.asset_obligation_rules?.rule_code ?? t('wo.checklist');

  // Rule column: frequency expressed as a sentence ("Every month", "6 years after manufacture").
  const freq = (o: Obl): string => {
    const r = o.asset_obligation_rules;
    if (!r) return '—';
    const months = r.interval_months ?? 0;
    const days = r.interval_days ?? 0;
    if (r.basis === 'manufacture') {
      if (months % 12 === 0 && months > 0) return t('freq.afterManufactureYears', { count: months / 12 });
      if (months > 0) return t('freq.afterManufactureMonths', { count: months });
    }
    if (months === 1) return t('freq.monthly');
    if (months === 12) return t('freq.yearly');
    if (months > 0) return t('freq.everyMonths', { count: months });
    if (days > 0) return t('freq.everyDays', { count: days });
    return '—';
  };

  // Activity: readable label ("Asset created", "Obligation updated") coloured by action type.
  const auditLabel = (a: Audit): string => {
    const subject = t('audit.subject.' + a.table_name, { defaultValue: a.table_name.replace(/_/g, ' ') });
    const action = t('audit.action.' + a.action, { defaultValue: a.action.replace(/_/g, ' ') });
    return `${subject} ${action}`;
  };

  if (!asset) return <Layout header={<Topbar />}><div className="p-8 text-muted">{t('common.loading')}</div></Layout>;

  const crit = CRITICALITY[asset.criticality];
  const avail = AVAILABILITY[asset.availability];
  const dkPill = 'rounded-pill px-2.5 py-1 text-xs font-medium';

  return (
    <Layout header={<Topbar />}>
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-[18px] px-7 py-6">
        {/* breadcrumb */}
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-muted">
          <span className="text-accent">{t('nav.properties')}</span><span>/</span>
          {asset.properties?.name && <><span className="text-accent">{asset.properties.name}</span><span>/</span></>}
          {asset.zones?.name && <><span className="text-accent">{asset.zones.name}</span><span>/</span></>}
          <span className="text-fg">{asset.serial_number ?? asset.name}</span>
        </nav>

        {/* dark hero */}
        <section className="flex flex-wrap items-center gap-6 rounded-[20px] bg-chrome p-6 text-white">
          <div className="grid h-28 w-28 shrink-0 place-items-center rounded-card border border-chrome-border bg-gradient-to-br from-chrome-3 to-chrome-2 text-xs text-chrome-muted">
            {t('field.asset')}
          </div>
          <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-2.5">
            <span className="font-mono text-xs text-chrome-muted">
              {asset.serial_number ?? asset.name} · QR <span className="text-accent-dark">{asset.qr_token}</span>
            </span>
            <h1 className="text-[28px] font-semibold tracking-[-0.02em]">{asset.name}</h1>
            <span className="text-chrome-muted">
              {asset.asset_categories?.name}{asset.agent_type ? ` · ${asset.agent_type}` : ''}
              {asset.manufacture_date ? ` · ${new Date(asset.manufacture_date).getFullYear()}` : ''}
            </span>
            <div className="flex flex-wrap gap-2">
              {crit && <span className={cn(dkPill, 'bg-dk-danger-bg text-dk-danger-fg')}>{t(crit.labelKey)}</span>}
              {avail && <span className={cn(dkPill, 'bg-dk-ok-bg text-dk-ok-fg')}>{t(avail.labelKey)}</span>}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {lifecycle && <Ring value={lifecycle.pct} size={96} stroke={9} onDark caption={t('asset.lifeUsed')} />}
            <div className="flex flex-col gap-2">
              <Button variant="secondary" onDark>{t('asset.printQr')}</Button>
              <Button variant="accent" onDark>+ {t('asset.newWorkOrder')}</Button>
            </div>
          </div>
        </section>

        {/* KPI row */}
        <section className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
          {[
            [t('asset.nextDue'), nextDue?.due_date ? fmtDate(nextDue.due_date, i18n.language, timezone) : '—', nextDue ? oblName(nextDue) : t('common.noData'), true],
            [t('asset.lastService'), lastDone?.last_completed_at ? fmtDate(lastDone.last_completed_at, i18n.language, timezone) : '—', lastDone ? oblName(lastDone) : t('common.noData')],
            [t('asset.endOfLife'), lifecycle ? String(lifecycle.endYear) : '—', lifecycle ? '' : t('common.noData')],
            [t('asset.costThisYear'), '—', t('common.noData')],
          ].map(([label, value, sub, accent], i) => (
            <Card key={i} className="flex flex-col gap-1.5 p-[18px]">
              <span className="text-xs text-muted">{label as string}</span>
              <span className="font-mono text-[22px] font-medium">{value as string}</span>
              <span className={cn('text-xs', accent ? 'text-accent' : 'text-muted')}>{sub as string}</span>
            </Card>
          ))}
        </section>

        <div className="flex flex-wrap items-start gap-[18px]">
          {/* obligations table */}
          <Card className="min-w-0 flex-[2_1_560px] overflow-hidden p-0">
            <div className="border-b border-divider p-[14px_18px]">
              <SegmentedControl items={[t('asset.dueDates'), t('asset.history'), t('asset.warranty'), t('asset.documents'), t('asset.costs')]} active={tab} onChange={setTab} />
            </div>
            {lifecycle && (
              <div className="flex flex-col gap-2 p-[18px_20px_8px]">
                <span className="text-xs text-muted">{t('asset.lifecycle')} · {lifecycle.startYear} → {lifecycle.endYear}</span>
                <div className="relative h-2 rounded-[4px] bg-divider">
                  <div className="h-2 rounded-[4px] bg-accent" style={{ width: `${lifecycle.pct}%` }} />
                </div>
              </div>
            )}
            {tab === 0 && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted">
                      <th className="px-5 py-3 font-medium">{t('asset.obligation')}</th>
                      <th className="px-2 py-3 font-medium">{t('asset.rule')}</th>
                      <th className="px-2 py-3 font-medium">{t('asset.nextDue')}</th>
                      <th className="px-5 py-3 font-medium">{t('asset.statusCol')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {obl.map((o) => {
                      const sd = OBLIGATION_STATUS[o.status] ?? OBLIGATION_STATUS.scheduled!;
                      return (
                        <tr key={o.id} className="border-t border-divider">
                          <td className="px-5 py-3.5 font-medium">{oblName(o)}</td>
                          <td className="px-2 py-3.5 text-muted">{freq(o)}</td>
                          <td className="px-2 py-3.5 font-mono">{o.due_date ? fmtDate(o.due_date, i18n.language, timezone) : '—'}</td>
                          <td className="px-5 py-3.5"><StatusPill tone={sd.tone}>{t(sd.labelKey)}</StatusPill></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {tab === 1 && (
              <div className="flex flex-col divide-y divide-divider p-2">
                {wos.length === 0 && <div className="p-4 text-sm text-muted">{t('assetTab.noHistory')}</div>}
                {wos.map((w) => { const sd = WORK_ORDER_STATUS[w.status]; return (
                  <div key={w.id} className="flex items-center justify-between gap-3 px-3 py-3 text-sm">
                    <span className="font-mono text-xs text-muted">{w.wo_number ?? '—'}</span>
                    <span className="flex-1">{t('woType.' + w.wo_type)}</span>
                    <span className="font-mono text-xs text-muted">{fmtDate(w.created_at, i18n.language, timezone)}</span>
                    {sd && <StatusPill tone={sd.tone}>{t(sd.labelKey)}</StatusPill>}
                  </div>
                ); })}
              </div>
            )}
            {tab === 2 && (
              <div className="flex flex-col divide-y divide-divider p-2">
                {warranties.length === 0 && <div className="p-4 text-sm text-muted">{t('assetTab.noWarranty')}</div>}
                {warranties.map((w) => (
                  <div key={w.id} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-3 py-3 text-sm">
                    <span className="font-medium">{w.guarantor ?? '—'}</span>
                    <span className="text-muted">{t('assetTab.starts')}: {w.starts_on ? fmtDate(w.starts_on, i18n.language, timezone) : '—'}</span>
                    <span className="text-muted">{t('assetTab.ends')}: {w.ends_on ? fmtDate(w.ends_on, i18n.language, timezone) : '—'}</span>
                  </div>
                ))}
              </div>
            )}
            {tab === 3 && (
              <div className="flex flex-col gap-2 p-4">
                <label className="inline-flex w-max cursor-pointer items-center gap-2 rounded-md border border-dashed border-[#B8C2FF] bg-[#F5F7FF] px-3 py-1.5 text-sm font-medium text-accent">
                  {uploading ? t('common.saving') : `+ ${t('assetTab.upload')}`}
                  <input type="file" className="hidden" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(f); }} />
                </label>
                {docs.length === 0 && <div className="text-sm text-muted">{t('assetTab.noDocuments')}</div>}
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between rounded-[10px] border border-divider px-3 py-2 text-sm">
                    <span className="truncate">{d.filename ?? d.kind}</span>
                    <span className="font-mono text-xs text-muted">{fmtDate(d.created_at, i18n.language, timezone)}</span>
                  </div>
                ))}
              </div>
            )}
            {tab === 4 && (
              <div className="flex flex-col gap-3 p-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted">{t('assetTab.costs')}</span>
                  <span className="font-mono text-[22px] font-medium">{formatCurrency(costTotal, currency, i18n.language)}</span>
                </div>
                <span className="text-xs text-muted">{t('cost.title')} — {wos.length} {t('nav.workOrders').toLowerCase()}</span>
              </div>
            )}
          </Card>

          {/* activity */}
          <Card className="min-w-0 flex-[1_1_300px] p-5">
            <h2 className="mb-4 text-[15px] font-semibold">{t('asset.activity')}</h2>
            <ol className="space-y-4">
              {(() => {
                // Merge the asset's maintenance history (work orders: inspections,
                // preventive, corrective…) with its audit trail into one feed.
                const feed = [
                  ...wos.map((w) => {
                    const sd = WORK_ORDER_STATUS[w.status];
                    return {
                      key: 'wo-' + w.id,
                      label: `${t('woType.' + w.wo_type)} · ${sd ? t(sd.labelKey) : w.status}`,
                      date: w.created_at,
                      dot: ['completed', 'validated', 'closed'].includes(w.status) ? 'var(--pv-ok)' : 'var(--pv-accent)',
                    };
                  }),
                  ...activity.map((a) => ({ key: a.id, label: auditLabel(a), date: a.changed_at, dot: AUDIT_TONE[a.action] ?? 'var(--pv-accent)' })),
                ].sort((x, y) => (y.date ?? '').localeCompare(x.date ?? '')).slice(0, 8);
                if (feed.length === 0) return <li className="text-sm text-muted">{t('common.noData')}</li>;
                return feed.map((f) => (
                  <li key={f.key} className="flex gap-3">
                    <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: f.dot, boxShadow: `0 0 0 4px color-mix(in srgb, ${f.dot} 15%, transparent)` }} />
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{f.label}</span>
                      <span className="font-mono text-xs text-muted">{fmtDate(f.date, i18n.language, timezone)}</span>
                    </div>
                  </li>
                ));
              })()}
            </ol>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
